import React, { useState } from "react";
import "./MarathonRegistration.css";

const API_URL = "http://localhost:5000/api/v1/marathon-registration";

const initialForm = {
  fullName: "",
  mobile: "",
  email: "",
  city: "",
  ageGroup: "",
  profilePhoto: null,
  aadhaarCard: null,
  emergencyName: "",
  emergencyMobile: "",
  relationship: "",
  participation: "",
  distance: "",
  tShirt: "",
  healthConditions: [],
  otherHealth: "",
  medicationHelp: "",
  assistanceDetails: "",
  safetyInstructions: "",
  followSafety: "",
  contactEmergency: "",
  attendance: "",
  participantDeclaration: false,
  dataConsent: false,
  photoConsent: false,
  signature: "",
  declarationDate: "",
  guardianName: "",
  guardianMobile: "",
  guardianSignature: "",
  guardianConsent: false,
};

const healthOptions = [
  "Heart condition / history of heart problems",
  "High blood pressure",
  "Diabetes / blood sugar condition",
  "Asthma or other breathing condition",
  "Seizures / epilepsy",
  "Known allergies (including medication or insect allergies)",
  "Recent surgery or injury",
  "Joint, bone, or mobility-related concern",
  "Other health condition (please specify below)",
  "None of the above",
  "Prefer not to disclose",
];

const steps = [
  "PARTICIPANT DETAILS",
  "EVENT & HEALTH",
  "CONSENT & DECLARATION",
];

function CompanyLogo({ footer = false }) {
  return (
    <a
      className={`marathon-brand${footer ? " footer-brand" : ""}`}
      href="/"
      aria-label="LifeOnPlus home"
    >
      <img
        className="lifeonplus-logo"
        src="/lifeon-new.svg.jpeg"
        alt="LifeOnPlus company logo"
      />
      <span className="brand-copy">
        <strong>LIFEONPLUS GLOBAL LIFELINE</strong>
        <small>RUN FOR SAFETY</small>
      </span>
    </a>
  );
}

export default function MarathonRegistration() {
  const [form, setForm] = useState({ ...initialForm });
  const [activeStep, setActiveStep] = useState(1);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const updateField = (event) => {
    const { name, value, type, checked, files } = event.target;

    setForm((previous) => {
      // Handle file uploads
      if (type === "file") {
        return {
          ...previous,
          [name]: files && files.length > 0 ? files[0] : null,
        };
      }

      // Handle health condition checkboxes
      if (name === "healthConditions" && type === "checkbox") {
        if (checked) {
          if (
            value === "None of the above" ||
            value === "Prefer not to disclose"
          ) {
            return { ...previous, healthConditions: [value] };
          }

          return {
            ...previous,
            healthConditions: [
              ...previous.healthConditions.filter(
                (item) =>
                  item !== "None of the above" &&
                  item !== "Prefer not to disclose"
              ),
              value,
            ],
          };
        }

        return {
          ...previous,
          healthConditions: previous.healthConditions.filter(
            (item) => item !== value
          ),
        };
      }

      return {
        ...previous,
        [name]: type === "checkbox" ? checked : value,
      };
    });

    setMessage("");
  };

  const validateStep = () => {
    if (activeStep === 1) {
      return Boolean(
        form.fullName.trim() &&
          form.mobile.trim() &&
          form.city.trim() &&
          form.ageGroup &&
          form.profilePhoto &&
          form.emergencyName.trim() &&
          form.emergencyMobile.trim() &&
          form.relationship.trim()
      );
    }

    if (activeStep === 2) {
      return Boolean(
        form.participation &&
          form.distance &&
          form.medicationHelp &&
          form.safetyInstructions &&
          form.followSafety === "Yes, I understand" &&
          form.contactEmergency
      );
    }

    return Boolean(
      form.attendance &&
        form.participantDeclaration &&
        form.dataConsent &&
        form.signature.trim() &&
        form.declarationDate &&
        (form.ageGroup !== "Under 18" ||
          (form.guardianName.trim() &&
            form.guardianMobile.trim() &&
            form.guardianSignature.trim() &&
            form.guardianConsent))
    );
  };

  const nextStep = () => {
    if (!validateStep()) {
      if (activeStep === 1 && !form.profilePhoto) {
        setMessage("Please upload your profile photo before continuing.");
      } else {
        setMessage("Please complete all required fields before continuing.");
      }
      return;
    }

    setMessage("");
    setActiveStep((step) => Math.min(step + 1, 3));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const previousStep = () => {
    setMessage("");
    setActiveStep((step) => Math.max(step - 1, 1));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!validateStep()) {
      setMessage("Please complete all required fields and consent.");
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      // FormData allows the form fields and uploaded files
      // to be submitted together.
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }

        if (key === "profilePhoto" || key === "aadhaarCard") {
          if (value instanceof File) {
            formData.append(key, value);
          }
          return;
        }

        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
          return;
        }

        formData.append(key, String(value));
      });

      const response = await fetch(API_URL, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || "Registration could not be submitted."
        );
      }

      setMessage("");
      setShowSuccessPopup(true);
      setForm({ ...initialForm });
      setActiveStep(1);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Marathon registration error:", error);

      setMessage(
        error.message ||
          "Unable to connect to the server. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const field = (
    label,
    name,
    placeholder = "",
    required = false,
    type = "text"
  ) => (
    <label className="field" key={name}>
      <span className="field-label">
        {label} {required && <span className="required-star">*</span>}
      </span>

      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={updateField}
        placeholder={placeholder}
        required={required}
      />
    </label>
  );

  const fileField = (
    label,
    name,
    accept,
    required = false,
    hint = ""
  ) => (
    <label className="field" key={name}>
      <span className="field-label">
        {label}{" "}
        {required ? (
          <span className="required-star">*</span>
        ) : (
          <span className="optional-label">(Optional)</span>
        )}
      </span>

      {hint && <span className="field-hint">{hint}</span>}

      <input
        type="file"
        name={name}
        accept={accept}
        onChange={updateField}
        required={required}
      />

      {form[name] && (
        <span className="field-hint">
          Selected: {form[name].name}
        </span>
      )}
    </label>
  );

  const options = (label, name, values, required = false) => (
    <fieldset className="field option-field" key={name}>
      <legend>
        {label} {required && <span className="required-star">*</span>}
      </legend>

      {values.map((item) => {
        const optionValue = item.value || item;
        const optionLabel = item.label || item;

        return (
          <label className="choice-option" key={optionValue}>
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={form[name] === optionValue}
              onChange={updateField}
            />
            <span>{optionLabel}</span>
          </label>
        );
      })}
    </fieldset>
  );

  const checkboxConsent = (name, text, required = false) => (
    <label className="consent-option" key={name}>
      <input
        type="checkbox"
        name={name}
        checked={form[name]}
        onChange={updateField}
      />
      <span>
        {text} {required && <b className="required-star">*</b>}
      </span>
    </label>
  );

  return (
    <main className="marathon-page">
      {/* SUCCESS POPUP */}
      {showSuccessPopup && (
        <div
          className="success-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-popup-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowSuccessPopup(false);
            }
          }}
        >
          <div className="success-popup">
            <div className="success-popup-icon">✓</div>

            <p className="section-eyebrow">
              LIFEONPLUS · RUN FOR SAFETY
            </p>

            <h2 id="success-popup-title">
              Registration Successful!
            </h2>

            <p>
              Your marathon registration has been submitted successfully.
              Thank you for joining LifeOnPlus Run for Safety.
            </p>

            <button
              type="button"
              className="gold-button"
              onClick={() => setShowSuccessPopup(false)}
              autoFocus
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* NAVIGATION */}
      <nav className="marathon-nav">
        <CompanyLogo />

        <div className="marathon-nav-links">
          <a href="#race-info">EVENT DETAILS</a>
          <a href="#registration-form">REGISTRATION</a>
          <a href="#race-footer">SAFETY & PRIVACY</a>
        </div>

        <span className="nav-edition">2026 EDITION</span>
      </nav>

      {/* HERO */}
      <section className="marathon-hero">
        <div className="hero-overlay" />

        <div className="hero-content">
          <p className="gold-eyebrow">
            <span />
            LIFEONPLUS GLOBAL LIFELINE
          </p>

          <h1>
            RUN FOR
            <br />
            <span>SAFETY.</span>
          </h1>

          <p className="hero-description">
            One community. One shared purpose. Join LifeOnPlus for Run for
            Safety and help us prepare for a safer, more supported event.
          </p>

          <a href="#registration-form" className="gold-button">
            REGISTER YOUR PARTICIPATION <span>↗</span>
          </a>

          <div className="hero-caption">
            <span className="caption-line" />
            EVERY STEP COUNTS
          </div>
        </div>

        <div className="hero-vertical-label">
          SAFETY · COMMUNITY · WELLBEING
        </div>
      </section>

      {/* EVENT DETAILS */}
      <section className="race-strip" id="race-info">
        <div>
          <span className="race-label">EVENT DATE</span>
          <strong>
            24 <small>OCT</small>
          </strong>
          <span className="race-note">
            2026 · Confirm with organiser
          </span>
        </div>

        <div>
          <span className="race-label">START TIME</span>
          <strong>
            7:30 <small>AM</small>
          </strong>
          <span className="race-note">Bengaluru, Karnataka</span>
        </div>

        <div>
          <span className="race-label">ORGANISED BY</span>
          <strong className="experience-title">LIFEONPLUS</strong>
          <span className="race-note">Run for Safety</span>
        </div>
      </section>

      {/* REGISTRATION SECTION */}
      <section className="registration-section" id="registration-form">
        <div className="registration-intro">
          <p className="section-eyebrow">
            YOUR JOURNEY STARTS HERE
          </p>

          <h2>
            Run with
            <br />
            <span>purpose.</span>
          </h2>

          <p>
            Please complete this form to register your interest or confirm
            participation and help the event team prepare safety and emergency
            support. Provide accurate contact details. Share only health
            information relevant to safe participation.
          </p>

          <div className="intro-divider" />

          <div className="intro-note">
            <span className="note-icon">✳</span>
            <div>
              <strong>SAFETY COMES FIRST</strong>
              <p>
                Registration is subject to confirmation by the event organiser.
              </p>
            </div>
          </div>
        </div>

        <div className="registration-card">
          <div className="form-heading">
            <div>
              <p className="section-eyebrow">OFFICIAL ENTRY FORM</p>
              <h3>Participant registration</h3>
            </div>

            <span className="form-edition">2026 / ENTRY</span>
          </div>

          {/* STEP PROGRESS */}
          <div className="step-progress">
            {steps.map((label, index) => {
              const step = index + 1;

              return (
                <div
                  className={`progress-step ${
                    activeStep >= step ? "is-active" : ""
                  }`}
                  key={step}
                >
                  <span className="step-number">
                    {activeStep > step ? "✓" : `0${step}`}
                  </span>
                  <span className="step-label">{label}</span>
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* STEP 1 */}
            {activeStep === 1 && (
              <div className="form-panel">
                <div className="panel-heading">
                  <span>A</span>
                  <div>
                    <h4>Participant Details</h4>
                    <p>
                      Complete your contact and emergency information.
                    </p>
                  </div>
                </div>

                <div className="form-grid">
                  {field(
                    "Full name",
                    "fullName",
                    "Enter your full name",
                    true
                  )}

                  {field(
                    "Mobile number",
                    "mobile",
                    "Enter mobile number",
                    true,
                    "tel"
                  )}

                  {field(
                    "Email address (Optional)",
                    "email",
                    "you@example.com",
                    false,
                    "email"
                  )}

                  {field("City / town", "city", "Enter city or town", true)}

                  {fileField(
                    "Profile Photo",
                    "profilePhoto",
                    "image/*",
                    true,
                    "Upload a clear photo of yourself. Required."
                  )}

                  {fileField(
                    "Aadhaar Card",
                    "aadhaarCard",
                    "image/*,.pdf",
                    false,
                    "Optional. Upload only if required by the event organiser."
                  )}

                  {options(
                    "Age group",
                    "ageGroup",
                    [
                      {
                        value: "Under 18",
                        label: "Under 18 (parent/guardian consent required)",
                      },
                      "18–30 years",
                      "31–45 years",
                      "46–59 years",
                      "60 years and above",
                    ],
                    true
                  )}

                  {field(
                    "Emergency contact person's full name",
                    "emergencyName",
                    "Emergency contact full name",
                    true
                  )}

                  {field(
                    "Emergency contact mobile number",
                    "emergencyMobile",
                    "Enter contact mobile",
                    true,
                    "tel"
                  )}

                  {field(
                    "Relationship to participant",
                    "relationship",
                    "e.g. Parent, spouse, friend",
                    true
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {activeStep === 2 && (
              <div className="form-panel">
                <div className="panel-heading">
                  <span>B</span>
                  <div>
                    <h4>Event Participation</h4>
                    <p>Tell us how you plan to participate.</p>
                  </div>
                </div>

                <div className="form-grid">
                  {options(
                    "Participation category",
                    "participation",
                    ["Runner", "Walker", "Volunteer", "Event support team"],
                    true
                  )}

                  {options(
                    "Preferred / planned distance",
                    "distance",
                    [
                      "3 km",
                      "5 km",
                      "Other / to be confirmed by organiser",
                      "Not yet decided",
                    ],
                    true
                  )}

                  {options(
                    "T-shirt size, if provided (Optional)",
                    "tShirt",
                    ["S", "M", "L", "XL", "XXL", "Not required"]
                  )}
                </div>

                <div className="panel-heading health-heading">
                  <span>C</span>
                  <div>
                    <h4>Health Conditions & Emergency Support</h4>
                    <p>
                      Health details are requested only to support event safety
                      and emergency response. Do not enter detailed medical
                      records. Select all options that apply.
                    </p>
                  </div>
                </div>

                <fieldset className="field health-options">
                  <legend>
                    Do you have any health condition or concern the event
                    safety team should know about? (Optional)
                  </legend>

                  {healthOptions.map((item) => (
                    <label className="choice-option" key={item}>
                      <input
                        type="checkbox"
                        name="healthConditions"
                        value={item}
                        checked={form.healthConditions.includes(item)}
                        onChange={updateField}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="form-grid health-extra">
                  <label className="field full-width">
                    <span className="field-label">
                      Other health information the medical team should know
                      (Optional) — Share only information relevant to your
                      safety
                    </span>

                    <textarea
                      name="otherHealth"
                      value={form.otherHealth}
                      onChange={updateField}
                      rows="3"
                      placeholder="Share only relevant information"
                    />
                  </label>

                  {options(
                    "Do you carry essential medication or need assistance during the event?",
                    "medicationHelp",
                    ["Yes", "No", "Prefer not to say"],
                    true
                  )}

                  <label className="field">
                    <span className="field-label">
                      If yes, please describe any relevant assistance needs
                      (Optional)
                    </span>

                    <textarea
                      name="assistanceDetails"
                      value={form.assistanceDetails}
                      onChange={updateField}
                      rows="3"
                      placeholder="Describe assistance needs, if any"
                    />
                  </label>

                  {options(
                    "Have you read the event safety instructions?",
                    "safetyInstructions",
                    ["Yes", "No"],
                    true
                  )}

                  {options(
                    "Do you understand that you should follow organiser safety instructions and inform the event team if you feel unwell?",
                    "followSafety",
                    ["Yes, I understand"],
                    true
                  )}

                  {options(
                    "Can the event team contact your emergency contact if a medical emergency occurs?",
                    "contactEmergency",
                    ["Yes", "No"],
                    true
                  )}
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {activeStep === 3 && (
              <div className="form-panel">
                <div className="panel-heading">
                  <span>D</span>
                  <div>
                    <h4>Attendance Confirmation</h4>
                    <p>Confirm your participation status.</p>
                  </div>
                </div>

                {options(
                  "Participation confirmation",
                  "attendance",
                  [
                    {
                      value: "confirmed",
                      label: "Yes, I confirm my participation.",
                    },
                    {
                      value: "interest",
                      label:
                        "I am registering my interest; my participation is not yet confirmed.",
                    },
                  ],
                  true
                )}

                <div className="panel-heading consent-heading">
                  <span>E</span>
                  <div>
                    <h4>Consent & Participant Declaration</h4>
                    <p>Read the declarations carefully before agreeing.</p>
                  </div>
                </div>

                <div className="declaration-box">
                  <p className="review-label">
                    19. PARTICIPANT DECLARATION *
                  </p>
                  <p>
                    I confirm that the information provided is accurate to the
                    best of my knowledge. I understand that running or walking
                    involves physical activity and that I should participate
                    within my abilities and choose a level of activity
                    appropriate to my health. I will follow event rules and
                    safety instructions, and stop and seek assistance if I feel
                    unwell. I understand that registration does not replace
                    medical advice or medical clearance where needed.
                  </p>
                </div>

                {checkboxConsent(
                  "participantDeclaration",
                  "I have read and agree to the participant declaration above.",
                  true
                )}

                <div className="declaration-box">
                  <p className="review-label">
                    20. EVENT DATA AND EMERGENCY/HEALTH INFORMATION CONSENT *
                  </p>
                  <p>
                    I consent to LifeOnPlus using my registration details for
                    event administration, safety coordination, and event-related
                    communications. I also consent to authorised LifeOnPlus
                    event safety personnel using the emergency contact details
                    and relevant health information I provide for event safety
                    and emergency response. I understand that this information
                    should be accessed only by authorised personnel for these
                    purposes and handled in accordance with the applicable
                    privacy policy.
                  </p>
                </div>

                {checkboxConsent(
                  "dataConsent",
                  "I have read and agree to the event data and emergency/health information consent above.",
                  true
                )}

                {checkboxConsent(
                  "photoConsent",
                  "21. Photo/video consent (Optional) — I consent to the use of event photographs or videos featuring me for LifeOnPlus event communications and promotion."
                )}

                <div className="form-grid signature-fields">
                  <label className="field full-width">
                    <span className="field-label">
                      22. Typed participant signature (full legal name) *
                    </span>
                    <span className="field-hint">
                      Electronic acknowledgement; not necessarily a legally
                      recognised digital signature
                    </span>
                    <input
                      name="signature"
                      value={form.signature}
                      onChange={updateField}
                      placeholder="Enter full legal name"
                      required
                    />
                  </label>

                  <label className="field">
                    <span className="field-label">
                      23. Date of declaration{" "}
                      <span className="required-star">*</span>
                    </span>
                    <input
                      type="date"
                      name="declarationDate"
                      value={form.declarationDate}
                      onChange={updateField}
                      required
                    />
                  </label>
                </div>

                <div className="panel-heading guardian-heading">
                  <span>F</span>
                  <div>
                    <h4>Parent / Guardian Consent</h4>
                    <p>Required for participants under 18.</p>
                  </div>
                </div>

                <div className="form-grid">
                  {field(
                    "24. Parent / guardian full name (Optional)",
                    "guardianName",
                    "Parent / guardian full name"
                  )}

                  {field(
                    "25. Parent / guardian mobile number (Optional)",
                    "guardianMobile",
                    "Parent / guardian mobile",
                    false,
                    "tel"
                  )}

                  {field(
                    "26. Parent / guardian typed signature (Optional)",
                    "guardianSignature",
                    "Parent / guardian typed signature"
                  )}
                </div>

                <div className="declaration-box">
                  <p className="review-label">
                    27. PARENT / GUARDIAN CONSENT
                  </p>
                  <p>
                    I am the parent/legal guardian of the participant named
                    above and give permission for them to participate in the
                    LifeOnPlus Run for Safety event. I have reviewed the event
                    information and understand the physical activity and safety
                    considerations.
                  </p>
                </div>

                {checkboxConsent(
                  "guardianConsent",
                  "I give permission for the minor named above to participate.",
                  form.ageGroup === "Under 18"
                )}

                <div className="review-box safety-note">
                  <p className="review-label">
                    IMPORTANT SAFETY & PRIVACY NOTE
                  </p>
                  <p>
                    Health information is sensitive. Registration responses
                    should be accessible only to authorised staff, and only
                    necessary emergency details should be shared with authorised
                    medical personnel. A health declaration does not guarantee
                    that a participant is medically fit to run. The event
                    organiser should confirm the final event date, approved
                    route and distances, privacy wording, and any additional
                    requirements before publishing this form.
                  </p>
                </div>
              </div>
            )}

            {/* ALL VALIDATION / SERVER ERRORS IN RED */}
            {message && (
              <div
                className="form-message form-error-message"
                role="alert"
                aria-live="assertive"
              >
                <span className="form-error-icon" aria-hidden="true">
                  !
                </span>
                <span>{message}</span>
              </div>
            )}

            {/* FORM BUTTONS */}
            <div className="form-actions">
              {activeStep > 1 && (
                <button
                  className="back-button"
                  type="button"
                  onClick={previousStep}
                >
                  ← BACK
                </button>
              )}

              <span className="required-note">
                <span>*</span> Required fields
              </span>

              {activeStep < 3 ? (
                <button
                  className="gold-button form-submit"
                  type="button"
                  onClick={nextStep}
                >
                  CONTINUE <span>→</span>
                </button>
              ) : (
                <button
                  className="gold-button form-submit"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting
                    ? "SUBMITTING..."
                    : "COMPLETE REGISTRATION"}{" "}
                  <span>↗</span>
                </button>
              )}
            </div>
          </form>

          <p className="form-security">
            LIFEONPLUS · RUN FOR SAFETY · PARTICIPANT SAFETY FIRST
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="marathon-footer" id="race-footer">
        <CompanyLogo footer />
        <p>SAFETY · COMMUNITY · WELLBEING</p>
        <span>© 2026 · LIFEONPLUS</span>
      </footer>
    </main>
  );
}

